
import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useTasks } from './TaskContext';
import { useActiveTimeEntry } from '@/hooks/timer/useActiveTimeEntry';
import { useRealTimeTaskUpdate } from '@/hooks/timer/useRealTimeTaskUpdate';
import { timerReducer, initialTimerState } from '@/hooks/timer/timerReducer';
import { TimerContextType, TimerSettings } from '@/hooks/timer/timerTypes';
import { handleTimerCompletion } from '@/hooks/timer/timerCompletion';
import { useDocumentTitle } from '@/hooks/timer/useDocumentTitle';
import { 
  createStartAction, 
  createStopAction, 
  createResetAction, 
  createTickAction, 
  createCompleteAction,
  createUpdateSettingsAction,
  createChangeTypeAction 
} from '@/hooks/timer/timerActionCreators';

const TimerContext = createContext<TimerContextType | undefined>(undefined);

export const TimerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(timerReducer, initialTimerState);
  const { user } = useAuth();
  const { activeTask, updateTaskTime } = useTasks();
  const navigate = useNavigate();
  const { 
    startTimeEntry, 
    finalizeCurrentEntry, 
    isTracking,
    updateCurrentEntry 
  } = useActiveTimeEntry();

  // Update document title based on timer state
  useDocumentTitle(state.minutes, state.timerType);

  // Enable real-time task time updates - this will make the card update in real-time
  useRealTimeTaskUpdate(state.isRunning, state.timerType);

  // Timer tick effect
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (state.isRunning && (state.minutes > 0 || state.seconds > 0)) {
      interval = setInterval(() => {
        dispatch(createTickAction());
      }, 1000);
    } else if (state.isRunning && state.minutes === 0 && state.seconds === 0) {
      // Timer completed
      handleCompletion();
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [state.isRunning, state.minutes, state.seconds]);

  // Handle timer completion
  const handleCompletion = useCallback(async () => {
    const newCompletedPomodoros = await handleTimerCompletion({
      timerType: state.timerType,
      sequencePosition: state.sequencePosition,
      completedPomodoros: state.completedPomodoros,
      settings: state.settings,
      activeTask,
      updateTaskTime,
      resetTimer: (type, startRunning, sequencePos) => {
        dispatch(createResetAction(type, startRunning, sequencePos));
      },
      setIsFirstStart: (value) => {
        // This is handled by the reducer
      },
      setSessionIndex: (index) => {
        // This is handled by the reducer
      },
      user,
      finalizeCurrentEntry
    });

    dispatch(createCompleteAction(newCompletedPomodoros));
  }, [state, activeTask, updateTaskTime, user, finalizeCurrentEntry]);

  // Listen for start focus time events from anywhere in the app
  useEffect(() => {
    const handleStartFocusTime = async () => {
      console.log("TimerContext: Start focus time event received");
      try {
        // Switch to pomodoro if not already
        if (state.timerType !== 'pomodoro') {
          dispatch(createChangeTypeAction('pomodoro'));
        }
        
        // Reset and start the timer
        dispatch(createResetAction('pomodoro', true));
        
        // Navigate to the main page where the timer is visible
        navigate('/');
        
        console.log("TimerContext: Successfully started focus time and navigated to timer");
      } catch (error) {
        console.error("TimerContext: Error starting focus time:", error);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('startFocusTime', handleStartFocusTime);
      
      return () => {
        window.removeEventListener('startFocusTime', handleStartFocusTime);
      };
    }
  }, [state.timerType, navigate]);

  // Timer control functions
  const toggleTimer = useCallback(async () => {
    console.log('TimerContext: Toggle timer called, current state:', state.isRunning);
    
    if (state.isRunning) {
      // Stopping timer
      console.log('TimerContext: Stopping timer');
      dispatch(createStopAction());
      
      // Update current entry if tracking
      if (isTracking) {
        await updateCurrentEntry(true); // Force update on stop
      }
    } else {
      // Starting timer
      console.log('TimerContext: Starting timer');
      dispatch(createStartAction());
      
      // Start time tracking for pomodoro sessions
      if (state.timerType === 'pomodoro' && user && activeTask && !isTracking) {
        try {
          await startTimeEntry();
          console.log('Started continuous time tracking');
        } catch (error) {
          console.error('Failed to start time tracking:', error);
        }
      }
    }
  }, [state.isRunning, state.timerType, user, activeTask, isTracking, startTimeEntry, updateCurrentEntry]);

  const resetTimer = useCallback(async (type = state.timerType, startRunning = false, sequencePos = 0) => {
    console.log('TimerContext: Reset timer called:', { type, startRunning, sequencePos });
    
    // Finalize current entry if we're resetting during active tracking
    if (isTracking && state.isRunning) {
      try {
        await finalizeCurrentEntry('interrupted');
        console.log('Finalized interrupted session on reset');
      } catch (error) {
        console.error('Failed to finalize interrupted session:', error);
      }
    }

    dispatch(createResetAction(type, startRunning, sequencePos));
  }, [state.timerType, state.isRunning, isTracking, finalizeCurrentEntry]);

  const changeTimerType = useCallback(async (type: string) => {
    console.log('TimerContext: Change timer type called:', type);
    
    // Finalize current entry if switching timer types during active session
    if (isTracking && state.isRunning && state.timerType === 'pomodoro') {
      try {
        await finalizeCurrentEntry('interrupted');
        console.log('Finalized session due to timer type change');
      } catch (error) {
        console.error('Failed to finalize session on type change:', error);
      }
    }

    dispatch(createChangeTypeAction(type as any));
  }, [state.isRunning, state.timerType, isTracking, finalizeCurrentEntry]);

  const saveSettings = useCallback((newSettings: TimerSettings) => {
    dispatch(createUpdateSettingsAction(newSettings));
  }, []);

  // Navigation functions
  const goToPrevious = useCallback(() => {
    console.log('TimerContext: Go to previous called');
    const newPos = Math.max(0, state.sequencePosition - 1);
    const newType = newPos % 2 === 0 ? 'pomodoro' : 
                   (state.completedPomodoros % state.settings.sessions === 0 && newPos === state.settings.sessions * 2 - 1) ? 
                   'longBreak' : 'shortBreak';
    resetTimer(newType, false, newPos);
  }, [state.sequencePosition, state.completedPomodoros, state.settings.sessions, resetTimer]);

  const goToNext = useCallback(() => {
    console.log('TimerContext: Go to next called');
    const maxPos = state.settings.sessions * 2 - 1;
    const newPos = Math.min(maxPos, state.sequencePosition + 1);
    const newType = newPos % 2 === 0 ? 'pomodoro' : 
                   (newPos === maxPos) ? 'longBreak' : 'shortBreak';
    resetTimer(newType, false, newPos);
  }, [state.sequencePosition, state.settings.sessions, resetTimer]);

  // Calculate current session index
  const currentSessionIndex = Math.floor(state.sequencePosition / 2);

  // Debug timer state
  console.log('TimerContext state:', {
    minutes: state.minutes,
    seconds: state.seconds,
    isRunning: state.isRunning,
    progress: state.progress,
    timerType: state.timerType
  });

  const contextValue: TimerContextType = {
    // State
    minutes: state.minutes,
    seconds: state.seconds,
    isRunning: state.isRunning,
    timerType: state.timerType,
    settings: state.settings,
    completedPomodoros: state.completedPomodoros,
    sequencePosition: state.sequencePosition,
    currentSessionIndex,
    progress: state.progress,
    timerTitle: state.timerTitle,

    // Actions
    toggleTimer,
    resetTimer,
    changeTimerType,
    saveSettings,
    goToPrevious,
    goToNext
  };

  return (
    <TimerContext.Provider value={contextValue}>
      {children}
    </TimerContext.Provider>
  );
};

export const useTimerContext = () => {
  const context = useContext(TimerContext);
  if (context === undefined) {
    throw new Error('useTimerContext must be used within a TimerProvider');
  }
  return { timer: context };
};
